import React, { useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import { Button, Form, Table, Spinner } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { FaSearch, FaSync } from 'react-icons/fa';
import GraficaBarras from '../../components/GraficaBarras';

// Indicadores de Iniciativas: vista consolidada de las inscripciones en
// iniciativas, con filtros por cohorte, período, iniciativa y patrocinante.
// Pedido por José el 24/09/2026 (punto 13). Backend: IndicadoresIniciativasController.

const FILTROS_VACIOS = { cohorte: '', periodo_id: '', iniciativa_id: '', patrocinante_id: '' };

// {etiqueta: cantidad} -> [{nombre, cantidad, porcentaje}] para GraficaBarras.
const rangosComoFilas = (rangos, total) =>
  Object.entries(rangos || {}).map(([nombre, cantidad]) => ({
    nombre,
    cantidad,
    porcentaje: total > 0 ? Math.round((cantidad * 10000) / total) / 100 : 0,
  }));

const Tarjeta = ({ titulo, valor, detalle, color }) => (
  <div className="chart-box text-center" style={{ flex: '1 1 0', minWidth: '180px' }}>
    <div style={{ fontSize: '2rem', fontWeight: 700, color }}>{valor}</div>
    <div style={{ color: 'gray' }}>{titulo}</div>
    {detalle && <div className="small text-muted">{detalle}</div>}
  </div>
);

const ShowIndicadoresIniciativas = () => {
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const cargar = async (f = filtros) => {
    setCargando(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
      const { data } = await axios.get('/indicadores-iniciativas', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setDatos(data);
    } catch (e) {
      console.error('Error cargando indicadores de iniciativas:', e);
      setError('No se pudieron cargar los indicadores. Intente de nuevo.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(FILTROS_VACIOS); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const cambiar = (e) => setFiltros((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const limpiar = () => { setFiltros(FILTROS_VACIOS); cargar(FILTROS_VACIOS); };
  // Misma clase que resalta los filtros activos en Indicadores.
  const activo = (v) => (v ? 'is-active-filter' : '');

  const op = datos?.opciones || {};
  const t = datos?.totales || { inscripciones: 0, personas: 0, completados: 0 };
  const pctCompletado = t.inscripciones > 0 ? Math.round((t.completados * 1000) / t.inscripciones) / 10 : 0;

  const exportar = () => {
    if (!datos) return;
    const libro = XLSX.utils.book_new();
    const hoja = (nombre, filas) => XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(filas), nombre);
    const conFormato = (lista) => (lista || []).map((x) => ({ Nombre: x.nombre, Cantidad: x.cantidad, Porcentaje: `${x.porcentaje}%` }));

    hoja('Resumen', [
      { Indicador: 'Inscripciones', Valor: t.inscripciones },
      { Indicador: 'Personas', Valor: t.personas },
      { Indicador: 'Completados', Valor: t.completados },
      { Indicador: '% completado', Valor: `${pctCompletado}%` },
    ]);
    hoja('Por iniciativa', (datos.resumenPorIniciativa || []).map((r) => ({
      Iniciativa: r.iniciativa, Inscripciones: r.inscripciones, Personas: r.personas,
      Completados: r.completados, '% completado': `${r.porcentajeCompletado}%`,
    })));
    hoja('Por patrocinante', conFormato(datos.porPatrocinante));
    hoja('Por cohorte', conFormato(datos.porCohorte));
    hoja('Por período', conFormato(datos.porPeriodo));
    hoja('Por género', conFormato(datos.porGenero));
    hoja('Por edad', conFormato(rangosComoFilas(datos.rangoEdades, t.inscripciones)));

    const buffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'indicadores_iniciativas.xlsx');
  };

  return (
    <div className="row" style={{ marginTop: '40px' }}>
      <div className="col-lg-12">
        <div className="card-box" style={{ padding: '15px' }}>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: '10px' }}>
            <h2 style={{ fontSize: '1.8rem' }} className="mb-0">Indicadores de Iniciativas</h2>
            <div className="d-flex" style={{ gap: '8px' }}>
              <Button variant="secondary" onClick={limpiar} title="Quitar filtros"><FaSync /></Button>
              <Button variant="success" onClick={exportar} disabled={!datos} title="Exportar a Excel">
                <i className="bi bi-printer-fill"></i>
              </Button>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: '10px' }}>
            <Form.Select name="cohorte" value={filtros.cohorte} onChange={cambiar} className={activo(filtros.cohorte)} style={{ width: 'auto' }}>
              <option value="">Cohorte</option>
              {(op.cohorte || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
            <Form.Select name="periodo_id" value={filtros.periodo_id} onChange={cambiar} className={activo(filtros.periodo_id)} style={{ width: 'auto' }}>
              <option value="">Período</option>
              {(op.periodo || []).map((p) => <option key={p.id} value={p.id}>{p.descripcion}</option>)}
            </Form.Select>
            <Form.Select name="iniciativa_id" value={filtros.iniciativa_id} onChange={cambiar} className={activo(filtros.iniciativa_id)} style={{ width: 'auto' }}>
              <option value="">Iniciativa</option>
              {(op.iniciativa || []).map((i) => <option key={i.id} value={i.id}>{i.descripcion}</option>)}
            </Form.Select>
            <Form.Select name="patrocinante_id" value={filtros.patrocinante_id} onChange={cambiar} className={activo(filtros.patrocinante_id)} style={{ width: 'auto', maxWidth: '320px' }}>
              <option value="">Patrocinante</option>
              {(op.patrocinante || []).map((p) => <option key={p.id} value={p.id}>{p.nombre_patrocinante}</option>)}
            </Form.Select>
            <Button variant="info" onClick={() => cargar()} disabled={cargando}>
              {cargando ? <Spinner size="sm" animation="border" /> : <><FaSearch /> Buscar</>}
            </Button>
          </div>
          {error && <div className="text-danger small mb-2">{error}</div>}
        </div>

        {datos && (
          <>
            <div className="d-flex flex-wrap mt-3" style={{ gap: '20px' }}>
              <Tarjeta titulo="Inscripciones" valor={t.inscripciones} color="#0d6efd" />
              <Tarjeta titulo="Personas" valor={t.personas} detalle="una persona puede estar en varias iniciativas" color="#6f42c1" />
              <Tarjeta titulo="Completados" valor={t.completados} detalle={`${pctCompletado}% de las inscripciones`} color="green" />
            </div>

            <div className="chart-box mt-3" style={{ width: '100%' }}>
              <h4 style={{ fontSize: '1.2rem' }}>Resumen por Iniciativa</h4>
              <Table size="sm" bordered hover className="mb-0">
                <thead>
                  <tr><th>Iniciativa</th><th className="text-end">Inscripciones</th><th className="text-end">Personas</th>
                    <th className="text-end">Completados</th><th className="text-end">% completado</th></tr>
                </thead>
                <tbody>
                  {(datos.resumenPorIniciativa || []).length === 0 && (
                    <tr><td colSpan={5} className="text-muted">Sin datos para los filtros aplicados.</td></tr>
                  )}
                  {(datos.resumenPorIniciativa || []).map((r) => (
                    <tr key={r.iniciativa}>
                      <td>{r.iniciativa}</td>
                      <td className="text-end">{r.inscripciones}</td>
                      <td className="text-end">{r.personas}</td>
                      <td className="text-end">{r.completados}</td>
                      <td className="text-end">{r.porcentajeCompletado}%</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Contenedores flex y no .row/.col: EditVoluntariados.css redefine
                .row para toda la aplicación y rompe la grilla de Bootstrap. */}
            <div className="d-flex flex-wrap mt-3" style={{ gap: '20px' }}>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Iniciativa" datos={datos.porIniciativa} color="#8884d8" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Patrocinante" datos={datos.porPatrocinante} color="#ff7300" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Cohorte" datos={datos.porCohorte} color="#00C49F" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Período" datos={datos.porPeriodo} color="#82ca9d" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Género" datos={datos.porGenero} color="#ffc658" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Rango de Edad" datos={rangosComoFilas(datos.rangoEdades, t.inscripciones)} color="#6f42c1" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShowIndicadoresIniciativas;
